import { ReactElement, useRef, useState } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { Component as AccountMenuIcon } from 'icons/icon-circle--account.svg';
import { Component as ContactUsIcon } from 'icons/icon-ui--contact-us.svg';
import { Component as AdminIcon } from 'icons/icon-ui--admin.svg';
import { Component as FAQIcon } from 'icons/icon-ui--faqs.svg';
import { Component as PolygonIcon } from 'icons/icon-ui--polygon.svg';
import { AlertModal, AlertModalVariant } from '../shared/modal/AlertModal';
import { Button } from '../shared/button/Button';
import { Route } from '../../utils/config';
import Link from '../shared/link/Link';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useNotification } from '../../contexts/NotificationContext';
import styles from './AccountMenu.module.scss';
import { MenuSection } from '../menu-section/MenuSection';

export function AccountMenu(): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [modalVariant, setModalVariant] = useState<AlertModalVariant | null>(null);

  const logoutButtonRef = useRef<HTMLFormElement>(null);
  const { cart } = useMenuWithCart();
  const { hideNotification } = useNotification();
  const { currentUser } = useCurrentUser();
  const {
    isAdmin,
    isEmployee,
    hasFavoritesEnabled,
    hasGiveaways,
    giveawayCount,
  } = currentUser;

  const handleClickOutside = (e) => {
    const focusedElement = e.relatedTarget;
    const accountMenuElement = document.getElementById(
      'account-menu-container',
    );

    if (!accountMenuElement?.contains(focusedElement)) {
      setIsOpen(false);
    }
  };

  const handleLogOut = (e) => {
    e.preventDefault();

    if (cart?.priceSummary?.remainingMinimumAmount) {
      setModalVariant(AlertModalVariant.CART_BELOW_MINIMUM);
      return;
    }

    document.cookie = `account_creation_email=; path=/; expires=${new Date(0).toUTCString()}`;
    logoutButtonRef?.current?.submit();
  };

  return (
    <div className={styles.menu_container} id="account-menu-container">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={handleClickOutside}
        className={styles.dropdown_button}
      >
        <AccountMenuIcon width="30" focusable="false" />
      </button>

      {isOpen && (
        <>
          <div
            className={styles.menu_overlay}
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setIsOpen(false);
            }}
            role="button"
            tabIndex={0}
            aria-label="Close menu overlay"
          />
          <div className={styles.polygon_full_container}>
            <div className={styles.polygon_shape}>
              <PolygonIcon width={36} height={24} />
            </div>
            <ul className={`${styles.dropdown_menu_container} ${isAdmin ? '' : styles.not_admin}`}>
              <MenuSection
                isInHamburger={false}
                onClose={() => setIsOpen(false)}
              />
              <div className={styles.section_container}>
                <Link
                  href={Route.FAQS}
                  onModalClose={() => setIsOpen(false)}
                  data-cy="header-faqs"
                  className={({ isActive }) => ([
                    'c-nav-header__sublink',
                    isActive ? 'is-active' : '',
                  ].join(' '))}
                  reload
                >
                  <div className={styles.link_container}>
                    <FAQIcon />
                    FAQs
                  </div>
                </Link>
                <>
                  <Link
                    href={Route.CONTACT_US}
                    onModalClose={() => setIsOpen(false)}
                    data-cy="header-contact-us"
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                    reload
                  >
                    <div className={styles.link_container}>
                      <ContactUsIcon />
                      Contact Us
                    </div>
                  </Link>
                </>
              </div>

              {isAdmin && (
                <div className={`${styles.admin_wrapper} ${styles.admin_section_wrapper}`}>
                  <Link
                    href={Route.ADMIN}
                    onModalClose={() => setIsOpen(false)}
                    data-cy="header-admin"
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                    reload
                  >
                    <div className={styles.link_container}>
                      <AdminIcon />
                      Admin
                    </div>
                  </Link>
                </div>
              )}
              <div
                className={`${styles.logged_out_button_container} ${isAdmin ? '' : styles.not_admin}`}
              >
                <form
                  method="POST"
                  action="/users/sign_out"
                  onSubmit={handleLogOut}
                  ref={logoutButtonRef}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <Button
                    type="submit"
                    text="Log Out"
                    style={{
                      borderColor: '#DEE2E6',
                      color: '#212529',
                      fontWeight: 400,
                      borderRadius: '4px',
                      width: '171px',
                      background: '#FFF',
                    }}
                  />
                </form>
              </div>
            </ul>
          </div>
        </>
      )}
      {!!modalVariant && (
        <AlertModal
          variant={modalVariant}
          isOpen
          onClose={() => {
            setModalVariant(null);
            setIsOpen(false);
          }}
          onClick={() => {
            hideNotification();
            logoutButtonRef?.current?.submit();
          }}
        />
      )}
    </div>
  );
}
